import { z } from 'zod';
import { rowLevelValidation } from 'v5/platform/row-level-validation';
import {
  REPORT_ENTITY_TYPES,
  type ReportEntityType,
  type ReportTemplate,
  type ReportTemplateDraft,
  type StoredReportQuery
} from './reportTemplates.api';

const REPORT_ENTITY_TYPE_IDS = {
  contact: 1,
  callout: 2,
  shortlist: 4,
  callsheet: 5,
  production: 6,
  booking: 27
} satisfies Record<ReportEntityType, number>;

function parseJsonOrNull(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    return null;
  }
}

const reportEntityType$ = z.number().transform((id, ctx) => {
  const entityType = REPORT_ENTITY_TYPES.find(
    type => REPORT_ENTITY_TYPE_IDS[type] === id
  );
  if (entityType === undefined) {
    ctx.addIssue({ code: 'custom', message: `Unknown entity_type ${id}` });
    return z.NEVER;
  }
  return entityType;
});

/** v1 stored bare keys or picker items (`{ key, name, parentLabel, order, … }`). */
const storedField$ = z.union([
  z.string().transform(key => ({ key, name: null })),
  z
    .looseObject({ key: z.string(), name: z.string().nullish() })
    .transform(field => ({ key: field.key, name: field.name ?? null }))
]);

const storedFields$ = z.preprocess(
  value => (typeof value === 'string' ? parseJsonOrNull(value) : value),
  z.array(storedField$)
);

const storedDateFilter$ = z.looseObject({
  date_field: z.string().optional(),
  is_relative: z.boolean().optional(),
  relative: z.string().nullable().optional(),
  start: z.string().nullable().optional(),
  end: z.string().nullable().optional()
});

const emptyStoredQuery: StoredReportQuery = { filters: {}, date_filters: [] };

/** A missing, empty or unreadable query reads as "no filters"; the export can't run it either. */
const storedQuery$ = z.preprocess(
  value =>
    (typeof value === 'string' ? parseJsonOrNull(value) : value) ??
    emptyStoredQuery,
  z.looseObject({
    filters: z
      .record(z.string(), z.unknown())
      .nullish()
      .transform(filters => filters ?? {}),
    date_filters: z
      .array(storedDateFilter$)
      .nullish()
      .transform(dateFilters => dateFilters ?? [])
  })
);

export const reportTemplate$ = z.object({
  id: z.number(),
  name: z.string(),
  description: z.string().nullish(),
  entity_type: reportEntityType$,
  fields: storedFields$,
  query: storedQuery$,
  archived: z.boolean().nullish(),
  user_id: z.string().nullish(),
  created_at: z.coerce.date().nullish()
});

export const reportTemplatesListResponse$ = z.object({
  results: rowLevelValidation.withRowLevelValidation(reportTemplate$)
});

export const reportExportResponse$ = z.object({
  data: z.object({ accessUrl: z.string() })
});

export function reportTemplateFromHttp(
  http: z.output<typeof reportTemplate$>
): ReportTemplate {
  return {
    id: http.id,
    name: http.name,
    description: http.description ?? null,
    entityType: http.entity_type,
    fields: http.fields,
    query: http.query,
    archived: http.archived ?? false,
    userId: http.user_id ?? null,
    createdAt: http.created_at ?? null
  };
}

/** `fields` and `query` are TEXT columns holding JSON; the export needs `date_filters` present. */
export function reportTemplateDraftToHttp(draft: ReportTemplateDraft) {
  const { filters, date_filters, ...otherQueryKeys } = draft.query;
  return {
    name: draft.name,
    description: draft.description,
    entity_type: REPORT_ENTITY_TYPE_IDS[draft.entityType],
    fields: JSON.stringify(
      draft.fields.map(({ key, name }) => ({ key, name }))
    ),
    query: JSON.stringify({ filters, date_filters, ...otherQueryKeys })
  };
}
